import * as React from "react";
import { Alert, AlertColor, Box, CardContent } from "@mui/material";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import {
    useDeleteUserConsentMutation,
    useGetUserConsentMutation,
} from "../../../../services/cclTokenedGrandCentralApi";
import {
    DataGridPro,
    GridColumns,
    GridRenderCellParams,
    GridSelectionModel,
} from "@mui/x-data-grid-pro";
import { addCustomDataGridFilters } from "../../../../components/common/customDataGridFilters/customDataGridFilters";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import { AccessEventUserConsentDetails } from "../../../../services/types/accessEventTypes";

export type GrandCentralConsentCardProps = {
    gcAccountId: string;
    gcAccountEmail: string;
    imKey: string;
    isSuperAdmin: boolean;
};

const GrandCentralConsentCard: React.FC<GrandCentralConsentCardProps> = (props) => {
    const [selectedConsentIds, setSelectedConsentIds] = React.useState<number[]>([]);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const { logEvent } = useLogAccessEvent();

    const [getUserConsent, { data: consents, isSuccess, isLoading, isError }] =
        useGetUserConsentMutation();

    const [revokeConsent] = useDeleteUserConsentMutation();

    React.useEffect(() => {
        getUserConsent({
            grandCentralId: props.gcAccountId,
            imKey: props.imKey,
            consentIds: [] /* irrelevant for get */,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.gcAccountId, props.imKey]);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const revoke = () => {
        // save types for logging
        var consentTypes = consents
            ?.filter((c) => selectedConsentIds.includes(c.id))
            .map((c) => c.title);

        revokeConsent({
            grandCentralId: props.gcAccountId,
            imKey: props.imKey,
            consentIds: selectedConsentIds,
        })
            .unwrap()
            .then(() => {
                getUserConsent({
                    grandCentralId: props.gcAccountId,
                    imKey: props.imKey,
                    consentIds: [],
                });
                const evtData: AccessEventUserConsentDetails = {
                    imKey: props.imKey,
                    email: props.gcAccountEmail,
                    consentTypes: consentTypes ? consentTypes.join(", ") : "",
                };
                logEvent("ConsentDataDeleted", evtData);
                ShowSnackbarElement("Consent Data Deleted", "success");
            })
            .catch(() => {
                ShowSnackbarElement("Consent Data Not Deleted", "error");
            });
    };

    const COLUMNS = [
        {
            field: "createdDateTimeUtc",
            headerName: "Created",
            valueGetter: (params: any) => new Date(params.row.createdDateTimeUtc),
            type: "date",
            renderCell: (params: GridRenderCellParams) => (
                <span>{params.value.toLocaleDateString()}</span>
            ),
            width: 100,
        },
        {
            field: "active",
            headerName: "Status",
            width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <span>{params.value ? "Active" : "Inactive"}</span>
            ),
        },
        { field: "title", headerName: "Type", flex: 1 },
    ];

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <CclGenericAccordian headingText="Consent" sx={{ height: 1 }}>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
            <CardContent sx={{ pt: 0, pb: 1 }}>
                {isError || (isSuccess && consents !== undefined && consents?.length === 0) ? (
                    isError ? (
                        <Alert severity="error">Failed to retrieve consent information.</Alert>
                    ) : (
                        <Alert severity="info">No consent information for this user.</Alert>
                    )
                ) : (
                    <div>
                        <DataGridPro
                            getRowId={(row) => row.id}
                            rows={consents ?? []}
                            columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
                            autoHeight={true}
                            loading={isLoading}
                            hideFooter={true}
                            checkboxSelection={true}
                            onSelectionModelChange={(selectedIds: GridSelectionModel) => {
                                setSelectedConsentIds(selectedIds as number[]);
                            }}
                        />
                        {props.isSuperAdmin ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 2,
                                }}
                            >
                                <CclButton
                                    aria-label="add-enterprise-identity"
                                    size={"small"}
                                    onClick={revoke}
                                    sx={{ mr: 4 }}
                                    mode={"primary"}
                                    disabled={selectedConsentIds.length === 0}
                                >
                                    Delete Consent Data
                                </CclButton>
                            </Box>
                        ) : null}
                    </div>
                )}
            </CardContent>
        </CclGenericAccordian>
        </Box>
    );
};

export default GrandCentralConsentCard;
